/**
 * Vertical Oceans — minimum-order checkout gate.
 *
 * Prevents any delivery from containing a lone "minimum" variant unit (e.g. one
 * 500g). It validates both the initial delivery date and recurring subscription
 * schedules. A one-time item can accompany a one-time 500g on its initial date,
 * but it cannot satisfy a recurring 500g because it will not be present on future
 * deliveries. The gate is client-side only (Basic plan): it disables checkout
 * controls, shows a message, and has submit/click backstops.
 *
 * Exposes window.VOMinimumGate:
 *   - isBlocked(cart)      -> boolean (cart = a /cart.js-shaped object, or a
 *                            synthetic { items:[...] } for Buy Now pre-checks)
 *   - messages             -> { blockedMessage, browseAddons, buyNowBlocked, buyNowNoDate }
 *   - evaluate()           -> re-fetch /cart.js + apply UI (debounced)
 *
 * The Step 2 "Continue" and Step 3 "Checkout" CTAs are gated by
 * vo-cart-drawer.js#renderCta (it calls isBlocked(this.#cart)). This file gates
 * the legacy footer #checkout button (snippets/cart-summary.liquid, rendered in
 * both the drawer footer and the cart page footer) and runs the submit backstop.
 */
(function () {
  'use strict';

  var CONFIG = null;
  var RE = null;
  var debounceId = 0;

  function readConfig() {
    if (CONFIG) return CONFIG;
    var el = document.querySelector('script[type="application/json"][data-vo-minimum-config]')
      || document.querySelector('script[data-vo-minimum-config]');
    CONFIG = { enabled: true, variantMatch: '500g', messages: {} };
    if (el) {
      try {
        var parsed = JSON.parse(el.textContent);
        if (typeof parsed.enabled === 'boolean') CONFIG.enabled = parsed.enabled;
        if (typeof parsed.variantMatch === 'string') CONFIG.variantMatch = parsed.variantMatch;
        if (parsed.messages) CONFIG.messages = parsed.messages;
      } catch (e) {
        /* keep defaults */
      }
    }
    RE = new RegExp(escapeRegExp(CONFIG.variantMatch), 'i');
    return CONFIG;
  }

  function escapeRegExp(s) {
    return String(s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function isFeeLine(i) {
    var properties = i && i.properties;
    if (!properties) return false;
    if (Array.isArray(properties)) {
      return properties.some(function (entry) {
        return entry && (entry.key === '_shipping_fee' || entry.name === '_shipping_fee') && entry.value === 'true';
      });
    }
    return properties._shipping_fee === 'true';
  }

  function normalizeText(value) {
    return String(value == null ? '' : value).trim().toLowerCase().replace(/\s+/g, ' ');
  }

  /** Read a line-item property case-insensitively from either Shopify's object
   *  shape or an array of Storefront-style { key/name, value } entries. */
  function getLineProperty(item, names) {
    var properties = item && item.properties;
    if (!properties) return '';
    var wanted = names.map(normalizeText);
    if (Array.isArray(properties)) {
      for (var i = 0; i < properties.length; i++) {
        var entry = properties[i] || {};
        var entryName = normalizeText(entry.key || entry.name);
        if (wanted.indexOf(entryName) !== -1) return entry.value || '';
      }
      return '';
    }
    var keys = Object.keys(properties);
    for (var j = 0; j < keys.length; j++) {
      if (wanted.indexOf(normalizeText(keys[j])) !== -1) return properties[keys[j]] || '';
    }
    return '';
  }

  /** Normalize the date formats used by the PDP/offer ("23 Sep 2026") and cart
   *  attributes ("2026-09-23") so equivalent dates share one delivery group. */
  function deliveryDateKey(item) {
    var raw = String(getLineProperty(item, ['Delivery Date', 'Harvest Date']) || '').trim();
    if (!raw) return '__missing_date__';
    var iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (iso) return iso[1] + '-' + pad2(iso[2]) + '-' + pad2(iso[3]);
    var months = {
      jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
      jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12'
    };
    var dmy = raw.match(/^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})$/);
    if (dmy && months[dmy[2].slice(0, 3).toLowerCase()]) {
      return dmy[3] + '-' + months[dmy[2].slice(0, 3).toLowerCase()] + '-' + pad2(dmy[1]);
    }
    var mdy = raw.match(/^([A-Za-z]{3,9})\s+(\d{1,2}),?\s+(\d{4})$/);
    if (mdy && months[mdy[1].slice(0, 3).toLowerCase()]) {
      return mdy[3] + '-' + months[mdy[1].slice(0, 3).toLowerCase()] + '-' + pad2(mdy[2]);
    }
    return normalizeText(raw);
  }

  function pad2(value) {
    return String(value).padStart(2, '0');
  }

  /** Prefer the selling-plan option values (the actual cadence) over the plan ID:
   *  different products can use different plan IDs for the same frequency. Unknown
   *  schedules stay isolated so they cannot accidentally qualify as companions. */
  function recurringScheduleKey(item, index) {
    var allocation = item && item.selling_plan_allocation;
    if (!allocation) return '';
    var plan = allocation.selling_plan || {};
    var options = Array.isArray(plan.options) ? plan.options : [];
    var parts = [];
    for (var i = 0; i < options.length; i++) {
      var option = options[i] || {};
      var value = normalizeText(option.value);
      if (value) parts.push(normalizeText(option.name) + '=' + value);
    }
    if (parts.length) return parts.sort().join('|');
    if (plan.name) return 'name=' + normalizeText(plan.name);
    if (plan.id != null) return 'plan=' + String(plan.id);
    return 'unknown=' + String((item && item.key) || (item && item.variant_id) || index);
  }

  function addToGroup(groups, key, minimumVariant, quantity) {
    if (!groups[key]) groups[key] = { minimumQty: 0, otherQty: 0 };
    if (minimumVariant) groups[key].minimumQty += quantity;
    else groups[key].otherQty += quantity;
  }

  function hasLoneMinimum(groups) {
    var keys = Object.keys(groups);
    for (var i = 0; i < keys.length; i++) {
      var group = groups[keys[i]];
      if (group.minimumQty === 1 && group.otherQty === 0) return true;
    }
    return false;
  }

  /**
   * @param {{items?:Array, [k:string]:any}|null} cart
   * @returns {boolean}
   */
  function isBlocked(cart) {
    readConfig();
    if (!CONFIG.enabled) return false;
    var items = (cart && Array.isArray(cart.items)) ? cart.items : [];
    var initialDeliveries = Object.create(null);
    var recurringDeliveries = Object.create(null);
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (isFeeLine(it)) continue; // shipping-fee line is not a companion
      var qty = Number(it.quantity) || 0;
      if (qty <= 0) continue;
      var minimumVariant = RE.test(it.variant_title || '');
      var date = deliveryDateKey(it);

      // Every item physically arriving on the first date can accompany a 500g,
      // regardless of whether that companion is one-time or recurring.
      addToGroup(initialDeliveries, date, minimumVariant, qty);

      // Future subscription deliveries may only rely on items that recur on the
      // same initial date and cadence. One-time lines are intentionally excluded.
      var schedule = recurringScheduleKey(it, i);
      if (schedule) {
        addToGroup(recurringDeliveries, date + '::' + schedule, minimumVariant, qty);
      }
    }
    return hasLoneMinimum(initialDeliveries) || hasLoneMinimum(recurringDeliveries);
  }

  function applyFooter(blocked) {
    // Legacy footer checkout button (snippets/cart-summary.liquid #checkout).
    var checkoutBtn = document.getElementById('checkout');
    if (checkoutBtn) {
      if (blocked) {
        checkoutBtn.setAttribute('disabled', 'true');
        checkoutBtn.setAttribute('aria-disabled', 'true');
        checkoutBtn.classList.add('vo-minimum-blocked');
      } else {
        checkoutBtn.removeAttribute('disabled');
        checkoutBtn.removeAttribute('aria-disabled');
        checkoutBtn.classList.remove('vo-minimum-blocked');
      }
    }
    // Accelerated checkout buttons (Shopify Pay / Apple Pay / Google Pay). These submit
    // a SEPARATE Shopify form — not the cart-form `name="checkout"` button the backstop
    // below catches — so the lone-500g gate was bypassable by jumping to Step 3 and
    // paying via an accelerated button. Toggle them off the same way as #checkout.
    // (vo-cart.css already hides .additional-checkout-buttons except on Step 3, so only
    // removing [hidden] here lets CSS keep control when NOT blocked.)
    var accel = document.querySelectorAll('.additional-checkout-buttons');
    for (var i = 0; i < accel.length; i++) {
      if (blocked) {
        accel[i].setAttribute('hidden', 'true');
        accel[i].setAttribute('aria-disabled', 'true');
        accel[i].classList.add('vo-minimum-blocked');
      } else {
        accel[i].removeAttribute('hidden');
        accel[i].removeAttribute('aria-disabled');
        accel[i].classList.remove('vo-minimum-blocked');
      }
    }
    // Footer message element(s).
    var msgs = document.querySelectorAll('[data-vo-minimum-footer-message]');
    for (var i = 0; i < msgs.length; i++) {
      if (blocked) {
        if (CONFIG.messages.blockedMessage && !msgs[i].dataset.voMinimumReady) {
          msgs[i].textContent = CONFIG.messages.blockedMessage;
        }
        msgs[i].dataset.voMinimumReady = '1';
        msgs[i].removeAttribute('hidden');
      } else {
        msgs[i].setAttribute('hidden', 'true');
      }
    }
  }

  function evaluate() {
    readConfig();
    if (!CONFIG.enabled) {
      applyFooter(false);
      return;
    }
    fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        var blocked = isBlocked(cart);
        applyFooter(blocked);
      })
      .catch(function () { /* ignore — backstop submit listener still guards */ });
  }

  function debouncedEvaluate() {
    clearTimeout(debounceId);
    debounceId = setTimeout(evaluate, 120);
  }

  // Submit backstop: if a checkout submit slips through (e.g. a button re-rendered
  // between evaluate() and the click), prevent it while blocked.
  document.addEventListener('submit', function (event) {
    if (event.defaultPrevented) return;
    var submitter = event.submitter;
    // Accelerated checkout (Shopify Pay / Apple Pay) submits a Shopify-controlled form
    // whose submitter has no name="checkout" and the form is not #cart-form. Catch it
    // by origin: the submit originates from the .additional-checkout-buttons block.
    var fromAccel = event.target instanceof Element && event.target.closest('.additional-checkout-buttons');
    if (fromAccel) {
      if (!CONFIG || !CONFIG.enabled) return;
      if (document.querySelector('[data-vo-minimum-footer-message]:not([hidden])')) {
        event.preventDefault();
      }
      return;
    }
    if (!submitter) return;
    if (submitter.name !== 'checkout') return;
    // Only act on the cart form's checkout buttons.
    var formId = submitter.getAttribute('form');
    var form = formId ? document.getElementById(formId) : event.target;
    if (!form || form.id !== 'cart-form') return;
    if (!CONFIG || !CONFIG.enabled) return;
    // Re-check the live cart synchronously is not possible; trust the last evaluate.
    // To be safe, fetch synchronously is not available — block if the footer button
    // is currently flagged, else allow (evaluate() keeps it current).
    if (submitter.classList.contains('vo-minimum-blocked') || document.querySelector('[data-vo-minimum-footer-message]:not([hidden])')) {
      event.preventDefault();
    }
  }, true);

  // Capture-phase click guard: accelerated checkout buttons are injected by Shopify's
  // script and may re-render/re-enable between evaluate() runs. Block any click that
  // originates inside .additional-checkout-buttons while the gate is blocked, before
  // Shopify's own handler can start the accelerated checkout.
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented) return;
    if (!CONFIG || !CONFIG.enabled) return;
    var target = event.target instanceof Element ? event.target : null;
    if (!target || !target.closest('.additional-checkout-buttons')) return;
    if (document.querySelector('[data-vo-minimum-footer-message]:not([hidden])')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', evaluate);
  } else {
    evaluate();
  }
  document.addEventListener('shopify:cart:lines-update', debouncedEvaluate);

  window.VOMinimumGate = {
    isBlocked: isBlocked,
    get messages() { readConfig(); return CONFIG.messages; },
    evaluate: debouncedEvaluate,
  };
})();
